import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { recordings, type RecordingRow } from '@/api/recordings';
import { PageHeader } from '@/components/PageHeader';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth-store';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { ChevronLeft, GitBranch, Plus, PlusCircle, Pencil, Wand2 } from 'lucide-react';
import { formatRef } from '@/lib/formatRef';
import { AppendPrependDialog } from './AppendPrependDialog';
import { TtsEditDialog } from './TtsEditDialog';

// Roles permitted to append / prepend — same as the mutate gate on the
// backend router (super_admin, reseller_owner, office_manager).
const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

function fmtDuration(ms: number | null): string {
  if (ms == null) return '—';
  const sec = Math.round(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function fmtBytes(raw: string | null): string {
  if (raw == null) return '—';
  const n = Number(raw);
  if (!isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function RecordingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const accessToken = useAuth((s) => s.accessToken);
  const canMutate = CAN_MUTATE.includes(user.role);

  const [mode, setMode] = useState<'append' | 'prepend' | null>(null);
  const [editTts, setEditTts] = useState(false);

  const { data: row, isLoading, error } = useQuery({
    queryKey: ['recordings', id],
    queryFn: () => recordings.get(id!),
    enabled: !!id,
  });

  // If this recording is derived from another, fetch the base for display.
  const baseId = row?.append_base_id ?? row?.prepend_base_id ?? null;
  const { data: baseRow } = useQuery({
    queryKey: ['recordings', baseId],
    queryFn: () => recordings.get(baseId!),
    enabled: !!baseId,
  });

  if (isLoading) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }
  if (error || !row) {
    return (
      <div role="alert" className="text-sm text-red-500">
        Could not load recording.
        <Button variant="outline" size="sm" className="ms-3" onClick={() => navigate('/recordings')}>
          Back
        </Button>
      </div>
    );
  }

  const derivationLabel =
    row.append_base_id
      ? 'appended'
      : row.prepend_base_id
        ? 'prepended'
        : null;

  return (
    <>
      <PageHeader
        title={row.name}
        description={row.description ?? undefined}
        eyebrow={
          <Link
            to="/recordings"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Back to Recordings
          </Link>
        }
      />

      {/* Derived-from banner */}
      {derivationLabel && baseRow && (
        <DerivedBanner base={baseRow} mode={derivationLabel} />
      )}

      {/* Player */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <AudioPlayer src={recordings.audioUrl(row.id, accessToken)} />
        <dl className="mt-4 grid grid-cols-2 gap-y-1.5 gap-x-6 text-xs sm:grid-cols-4">
          <div>
            <dt className="text-slate-400">Duration</dt>
            <dd className="font-mono tabular-nums text-slate-700">{fmtDuration(row.duration_ms)}</dd>
          </div>
          <div>
            <dt className="text-slate-400">Size</dt>
            <dd className="text-slate-700">{fmtBytes(row.size_bytes)}</dd>
          </div>
          <div>
            <dt className="text-slate-400">Language</dt>
            <dd className="text-slate-700">{row.language?.toUpperCase() ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-400">Ref</dt>
            <dd className="font-mono text-slate-700">{formatRef(row.ref_number, 'REC')}</dd>
          </div>
        </dl>
      </section>

      {/* TTS script editor — only on TTS-sourced recordings. Clicking the
          pencil lets the user re-write the script and re-synthesize the
          audio in place. The recording's id stays the same, so any IVR
          nodes referencing it pick up the new audio on the next call. */}
      {canMutate && isTtsSourced(row) && !row.append_base_id && !row.prepend_base_id && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Wand2 className="h-4 w-4 text-slate-500" />
              TTS script
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditTts(true)}
              aria-label="Edit TTS script and re-synthesize the audio"
            >
              <Pencil className="me-1.5 h-3.5 w-3.5" />
              Edit text
            </Button>
          </div>
          {row.source_text ? (
            <pre className="whitespace-pre-wrap rounded border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-700 font-sans leading-relaxed">
              {row.source_text}
            </pre>
          ) : (
            <p className="text-xs italic text-slate-500">
              Original script wasn't recorded — likely a legacy TTS file. Click
              "Edit text" to add a script. Re-rendering will replace the audio
              with a fresh synthesis from your text.
            </p>
          )}
        </section>
      )}

      {/* Append / Prepend section */}
      {canMutate && (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
            <GitBranch className="h-4 w-4 text-slate-500" />
            Combine with another audio
          </div>
          <p className="mb-3 text-xs text-slate-500">
            Create a new recording that joins this one with another piece of audio. Your original recording stays untouched — a new row is created in the recordings library.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setMode('prepend')}
              aria-label={`Prepend audio before ${row.name}`}
            >
              <PlusCircle className="me-2 h-4 w-4" />
              Prepend audio
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setMode('append')}
              aria-label={`Append audio after ${row.name}`}
            >
              <Plus className="me-2 h-4 w-4" />
              Append audio
            </Button>
          </div>
        </section>
      )}

      {/* Dialog (mounted only when open so form state resets cleanly) */}
      {mode && (
        <AppendPrependDialog
          base={row}
          mode={mode}
          open={mode !== null}
          onOpenChange={(v) => { if (!v) setMode(null); }}
        />
      )}

      {/* TTS edit dialog */}
      {editTts && (
        <TtsEditDialog
          row={row}
          open={editTts}
          onOpenChange={setEditTts}
        />
      )}
    </>
  );
}

/**
 * True when the recording was produced via TTS synthesis (either via the
 * legacy `source='tts'` enum or the newer `created_via='tts'` field).
 */
function isTtsSourced(row: RecordingRow): boolean {
  return row.source === 'tts' || row.created_via === 'tts';
}

function DerivedBanner({ base, mode }: { base: RecordingRow; mode: 'appended' | 'prepended' }) {
  return (
    <div className="mb-6 flex items-center gap-3 rounded-md border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm text-blue-900">
      <GitBranch className="h-4 w-4 flex-shrink-0 text-blue-500" />
      <div className="min-w-0">
        Derived from{' '}
        <Link
          to={`/recordings/REC-${base.ref_number}`}
          className="font-medium underline decoration-blue-300 decoration-dotted underline-offset-2 hover:decoration-solid"
        >
          {base.name}
        </Link>{' '}
        <span className="text-blue-600">({mode})</span>
      </div>
    </div>
  );
}
